import React from 'react';
import { createPortal } from 'react-dom';
import styles from './MilestoneHoverPopup.module.scss';
import {
  formatEnhancedPopupText,
  ENHANCED_MILESTONE_STATUSES
} from '../SlideOutPanel/Milestones/milestoneConfigEnhanced';

interface MilestoneHoverPopupProps {
  milestone: {
    name: string;
    required: boolean;
    reviewType?: string; // Changed from typeOfReview to reviewType
    typeOfReview?: string; // Keep for backward compatibility
    status?: string;
    date?: string;
    meetingScheduledOn?: string;
    meetingScheduledAt?: string;
    approver?: string;
    approvedBy?: string;
    originalApprovedOn?: string;
    reApprovedBy?: string;
    reApprovedOn?: string;
    /** Who rejected it, when status is Not Approved (incl. async variant). */
    notApprovedBy?: string;
    secondaryDate?: string;
    milestoneAttributes?: {
      async: boolean;
      audited: boolean;
      approvalRequired: boolean;
    };
  };
  isVisible: boolean;
  position: { x: number; y: number };
  arrowDirection?: 'up' | 'down';
  horizontalAlign?: 'left' | 'center' | 'right';
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

const MilestoneHoverPopup: React.FC<MilestoneHoverPopupProps> = ({
  milestone,
  isVisible,
  position,
  arrowDirection = 'down',
  horizontalAlign = 'left',
  onMouseEnter,
  onMouseLeave
}) => {
  if (!isVisible) return null;

  // Helper function to get status badges
  const getStatusBadges = (): string[] => {
    const badges: string[] = [];

    // Add review type badge
    const reviewType = milestone.reviewType || milestone.typeOfReview; // Support both field names
    if (reviewType === 'Meeting') {
      badges.push('Meeting');
    } else if (reviewType === 'Async') {
      badges.push('Async');
    }

    // Process status to extract multiple relevant badges
    // Safely handle status - check if it's a string before calling string methods
    if (milestone.status && typeof milestone.status === 'string') {
      const cleanStatus = milestone.status
        .replace(/_/g, ' ') // Replace underscores with spaces
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Capitalize each word
        .join(' ');

      // Check for various status indicators (not mutually exclusive)
      if (cleanStatus.toLowerCase().includes('approved')) {
        if (cleanStatus.toLowerCase().includes('not approved')) {
          badges.push('Not Approved');
        } else if (
          cleanStatus.toLowerCase().includes('re-approved') ||
          cleanStatus.toLowerCase().includes('re approved')
        ) {
          badges.push('Re-Approved');
        } else {
          badges.push('Approved');
        }
      }

      if (cleanStatus.toLowerCase().includes('pending')) {
        badges.push('Pending');
      }

      if (cleanStatus.toLowerCase().includes('scheduled')) {
        badges.push('Scheduled');
      }

      if (cleanStatus.toLowerCase().includes('complete')) {
        badges.push('Complete');
      }

      if (cleanStatus.toLowerCase().includes('sent')) {
        badges.push('Sent');
      }

      if (cleanStatus.toLowerCase().includes('target')) {
        badges.push('Target');
      }

      if (
        cleanStatus.toLowerCase().includes('follow-up') ||
        cleanStatus.toLowerCase().includes('follow up')
      ) {
        badges.push('Follow-up');
      }

      if (
        cleanStatus.toLowerCase().includes('async') &&
        !badges.includes('Async')
      ) {
        badges.push('Async');
      }

      if (cleanStatus.toLowerCase().includes('replanning')) {
        badges.push('Replanning');
      }

      // If none of the above matched, add the clean status as a badge
      if (
        badges.filter(badge => badge !== 'Meeting' && badge !== 'Async')
          .length === 0
      ) {
        badges.push(cleanStatus);
      }
    }

    // If no badges, show a default
    if (badges.length === 0) {
      badges.push('Status: TBD');
    }

    return badges;
  };

  // Get approval details
  const getApprovalDetails = (): { label: string; value: string }[] => {
    const details: { label: string; value: string }[] = [];

    // Approved on date
    if (milestone.date || milestone.originalApprovedOn) {
      details.push({
        label: 'Approved on:',
        value: milestone.date || milestone.originalApprovedOn || ''
      });
    }

    // Approver
    if (milestone.approver || milestone.approvedBy) {
      details.push({
        label: 'Approver:',
        value: milestone.approver || milestone.approvedBy || ''
      });
    }

    // Show fallback info if no approval details
    if (details.length === 0) {
      if (milestone.date) {
        details.push({
          label: 'Date:',
          value: milestone.date
        });
      }
      if (milestone.status) {
        // Safely convert status to string if it's not already
        const statusValue =
          typeof milestone.status === 'string'
            ? milestone.status
            : String(milestone.status);
        details.push({
          label: 'Status:',
          value: statusValue
        });
      }
    }

    return details;
  };

  const statusBadges = getStatusBadges();
  const approvalDetails = getApprovalDetails();

  /**
   * Once a milestone reaches a concluded status (Approved / Re-Approved / Not
   * Approved), the generic "Approver:" + pool-of-designated-approvers line
   * below is misleading — only one of them actually acted. Mirrors the same
   * resolution EditMilestoneModal's top Approver field uses (see
   * `approverResolution` there) so the hover popup and the edit form agree on
   * who gets shown and how it's labeled.
   */
  const approverResolution = (() => {
    const statusLower = (milestone.status || '')
      .toLowerCase()
      .replace(/_async$/, '');
    if (statusLower === 're_approved') {
      return { label: 'Re-Approved By:', value: milestone.reApprovedBy || '' };
    }
    if (statusLower === 'approved') {
      return { label: 'Approved By:', value: milestone.approvedBy || '' };
    }
    if (statusLower === 'not_approved') {
      return { label: 'Rejected By:', value: milestone.notApprovedBy || '' };
    }
    return null;
  })();

  const popupContent = (
    <div
      className={`${styles.hoverPopup} ${
        arrowDirection === 'up' ? styles.arrowUp : styles.arrowDown
      } ${
        horizontalAlign === 'center'
          ? styles.alignCenter
          : horizontalAlign === 'right'
          ? styles.alignRight
          : styles.alignLeft
      }`}
      style={{
        left: position.x,
        top: position.y
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className={styles.upDown}>
        <div className={styles.detailContent}>
          {/* Arrow pointer */}
          <div
            className={`${styles.arrowPointer} ${
              arrowDirection === 'up'
                ? styles.arrowPointerUp
                : styles.arrowPointerDown
            }`}
          >
            <div
              className={`${styles.arrowShape} ${
                arrowDirection === 'up'
                  ? styles.arrowShapeUp
                  : styles.arrowShapeDown
              }`}
            ></div>
          </div>

          {/* Icons + Title Section */}
          <div className={styles.iconsTitle}>
            <div className={styles.titleRow}>
              <div className={styles.milestoneTitle}>
                <span className={styles.milestoneName}>{milestone.name}</span>
                <div className={styles.titleIndicators}>
                  {milestone.milestoneAttributes?.approvalRequired && (
                    <span className={styles.approvalIndicator}>A</span>
                  )}
                  {milestone.milestoneAttributes?.approvalRequired && (
                    <span className={styles.approvalText}>
                      (Approval Required)
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Tags and Enhanced Popup Text as per Figma */}
            <div className={styles.contentSection}>
              {(() => {
                const reviewType =
                  milestone.reviewType || milestone.typeOfReview;

                const enhancedPopupText = formatEnhancedPopupText(
                  milestone.status || '',
                  milestone.milestoneAttributes?.approvalRequired || false, // Use milestone attributes instead of milestone.required
                  reviewType,
                  milestone.date,
                  milestone.meetingScheduledAt,
                  milestone.secondaryDate,
                  milestone.approver || milestone.approvedBy
                );

                return (
                  <>
                    {/* Tags Section - Review Type and Status as per Figma */}
                    <div className={styles.tagsSection}>
                      {reviewType && (
                        <div className={styles.tag}>{reviewType}</div>
                      )}
                      {milestone.status && (
                        <div className={styles.tag}>
                          {(() => {
                            // Get the display label from configuration instead of raw status value
                            // Safely convert status to uppercase for config lookup only
                            if (
                              !milestone.status ||
                              typeof milestone.status !== 'string'
                            ) {
                              return milestone.status || 'TBD';
                            }
                            const statusKey = milestone.status.toUpperCase();
                            const statusConfig =
                              ENHANCED_MILESTONE_STATUSES[statusKey];
                            return statusConfig?.label || milestone.status;
                          })()}
                        </div>
                      )}
                    </div>

                    {/* Rollover Copy Text below tags */}
                    {enhancedPopupText && enhancedPopupText.trim() && (
                      <div className={styles.rolloverText}>
                        {enhancedPopupText.split('\n').map((line, index) => {
                          // First try to split by "on" (e.g., "Meeting Scheduled on Feb 19, 2025")
                          const onIndex = line.indexOf(' on ');
                          if (onIndex !== -1) {
                            const label = line
                              .substring(0, onIndex + 3) // Include "on" with the label
                              .trim();
                            const value = line.substring(onIndex + 4).trim(); // Everything after "on "
                            return (
                              <div key={index} className={styles.rolloverLine}>
                                <span className={styles.rolloverLabel}>
                                  {label}
                                </span>
                                <span className={styles.rolloverValue}>
                                  {value}
                                </span>
                              </div>
                            );
                          }

                          // Fallback: Split on colon to separate label and value like "Sent on: Feb 19, 2025"
                          const colonIndex = line.indexOf(':');
                          if (colonIndex !== -1) {
                            const label = line
                              .substring(0, colonIndex + 1)
                              .trim();
                            const value = line.substring(colonIndex + 1).trim();
                            return (
                              <div key={index} className={styles.rolloverLine}>
                                <span className={styles.rolloverLabel}>
                                  {label}{' '}
                                </span>
                                <span className={styles.rolloverValue}>
                                  {value}
                                </span>
                              </div>
                            );
                          } else {
                            return (
                              <div key={index} className={styles.rolloverLine}>
                                {line}
                              </div>
                            );
                          }
                        })}

                        {/* ⭐ Show "Approved By" ONLY if status config indicates showApprover: true */}
                        {(() => {
                          // Check status configuration to see if approver should be shown
                          // Safely convert status to uppercase for config lookup only
                          const statusConfig =
                            milestone.status &&
                            typeof milestone.status === 'string'
                              ? ENHANCED_MILESTONE_STATUSES[
                                  milestone.status.toUpperCase()
                                ]
                              : null;

                          if (!statusConfig?.showApprover) return null;

                          // Once concluded (Approved/Re-Approved/Not Approved),
                          // show who actually acted instead of the full pool of
                          // designated approvers — see `approverResolution` above.
                          if (approverResolution) {
                            return approverResolution.value ? (
                              <div className={styles.rolloverLine}>
                                <span className={styles.rolloverLabel}>
                                  {approverResolution.label}{' '}
                                </span>
                                <span className={styles.rolloverValue}>
                                  {approverResolution.value}
                                </span>
                              </div>
                            ) : null;
                          }

                          // Live/current assignment takes priority over a stale
                          // `approvedBy` left behind by an earlier, now-superseded
                          // approval cycle (e.g. reopened to Sent and reassigned) —
                          // concluded statuses never reach this branch at all, they
                          // return via `approverResolution` above.
                          const shouldShowApprover =
                            milestone.approver || milestone.approvedBy;

                          return shouldShowApprover ? (
                            <div className={styles.rolloverLine}>
                              <span className={styles.rolloverLabel}>
                                Approver:{' '}
                              </span>
                              <span className={styles.rolloverValue}>
                                {milestone.approver || milestone.approvedBy}
                              </span>
                            </div>
                          ) : null;
                        })()}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Render using a portal to avoid stacking context issues with the sliding panel
  return createPortal(popupContent, document.body);
};

export default MilestoneHoverPopup;
